/**
 * How many customer logos an order may carry, and how big they may be.
 *
 * # Why the numbers live here
 *
 * Chapter 10 (F15) turned the single customer logo into a set. Three places
 * have to agree about the limits, and they are in three different workspaces:
 *
 *   - the public checkout, which must refuse a file at PICK time (so the
 *     customer fixes it before submitting, not after);
 *   - `POST /api/send/logo`, which enforces them for real on untrusted input;
 *   - the submission schema, which bounds the stored array.
 *
 * A copy in each would drift, and the failure mode of drift is nasty under the
 * all-or-nothing upload rule: a file the picker accepts but the server refuses
 * costs the customer EVERY logo on that order, not just the bad one.
 *
 * # The numbers, and why
 *
 * `MAX_LOGO_BYTES` (2 MB) is the pre-existing LOCKED owner cap for a customer
 * logo — a brand mark is small; 2 MB is already generous for a PNG. It moved
 * here from the route so the client can enforce the same number.
 *
 * `MAX_LOGO_COUNT` (5) answers the request that prompted F15: "there are
 * companies with more than one logo". A company has a primary mark, a
 * wordmark, a mono/inverted variant and occasionally a co-brand. Five covers
 * that with room to spare. Beyond five it is an asset library, not an order
 * attachment, and the honest channel is email to the sales contact.
 *
 * `MAX_LOGOS_TOTAL_BYTES` (6 MB) is the cap that actually bounds an order. The
 * count and the per-file cap together would admit 10 MB of anonymous upload
 * per submission; 6 MB still allows three full-size files or a realistic set
 * of five, while keeping one submission's worst case under the 10 MB the
 * shared media path is sized for.
 */

/** LOCKED owner cap for ONE customer logo. */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

/** Most logos one order may carry. */
export const MAX_LOGO_COUNT = 5;

/** Most bytes one order's logos may total. */
export const MAX_LOGOS_TOTAL_BYTES = 6 * 1024 * 1024;

/** Which limit a rejected selection broke. */
export type LogoSelectionRejection =
  | 'too-many'
  | 'file-too-large'
  | 'total-too-large';

/**
 * The verdict on a set of picked files. `ok` selections carry nothing else;
 * rejections carry a reason to branch on and a message safe to show verbatim.
 */
export type LogoSelectionCheck =
  | { ok: true; reason?: undefined; message?: undefined }
  | { ok: false; reason: LogoSelectionRejection; message: string };

/** Only what the check needs — so a caller can pass a `File` or a stub. */
export interface LogoSelectionEntry {
  readonly name: string;
  readonly size: number;
}

function mb(bytes: number): string {
  // One decimal, dot-free of locale concerns: this string goes into an error
  // the customer reads, and "6 MB" must not render as "6,0 MB" in one place
  // and "6.0 MB" in another.
  const value = bytes / (1024 * 1024);
  return `${Number.isInteger(value) ? value : value.toFixed(1)} MB`;
}

/**
 * Check a picked set of logos against all three limits.
 *
 * The count is reported FIRST when several limits are broken: "attach fewer
 * logos" is actionable, whereas naming one of six files to shrink is not.
 */
export function checkLogoSelection(
  files: readonly LogoSelectionEntry[],
): LogoSelectionCheck {
  if (files.length > MAX_LOGO_COUNT) {
    return {
      ok: false,
      reason: 'too-many',
      message: `You can attach up to ${MAX_LOGO_COUNT} logos. Remove ${
        files.length - MAX_LOGO_COUNT
      } and email any extras to your contact.`,
    };
  }
  const tooBig = files.find((f) => f.size > MAX_LOGO_BYTES);
  if (tooBig) {
    return {
      ok: false,
      reason: 'file-too-large',
      message: `"${tooBig.name}" is ${mb(tooBig.size)} — the limit per logo is ${mb(
        MAX_LOGO_BYTES,
      )}. Pick a smaller version of that file.`,
    };
  }
  const total = files.reduce((sum, f) => sum + f.size, 0);
  if (total > MAX_LOGOS_TOTAL_BYTES) {
    return {
      ok: false,
      reason: 'total-too-large',
      message: `Those logos come to ${mb(total)} together — the limit for one order is ${mb(
        MAX_LOGOS_TOTAL_BYTES,
      )}. Remove one, or use smaller files.`,
    };
  }
  return { ok: true };
}
